class NewsletterPopup extends ModalDialog {
  constructor() {
    super();
    this.init();
    this.attachEventListeners();
  }

  // Moved all event listeners to a separate method for better organization
  attachEventListeners() {
    this.buttonClose = this.querySelector(".js-button-close");
    this.buttonClose.addEventListener("click", () => this.closeNewsletter("button"));

    // Any newsletter form (popup, footer…): remember the sign-up so the code can be shown after the reload.
    document.addEventListener("submit", this.onSubscribe.bind(this));

    this.addEventListener("click", (event) => {
      if (event.target === this) {
        this.closeNewsletter("overlay");
      }
    });

    // Escape: ModalDialog only hears it with the focus inside, and the popup that opens by itself
    // leaves the focus on the page, so listen on the document. Remembered for this visit only.
    document.addEventListener("keyup", (event) => {
      if (event.code?.toUpperCase() === "ESCAPE" && (this.hasAttribute("open") || this.closingForm)) {
        this.closeNewsletter("escape");
      }
    });

    // Other "10€ OFF" triggers (e.g. the footer highlight with "Opens the newsletter popup"). The
    // announcement bar has its own listener and stops the click.
    document.addEventListener("click", (event) => {
      const trigger = event.target.closest?.("[data-open-newsletter-popup]");
      if (!trigger || this.contains(trigger)) return;
      event.preventDefault();
      this.openFromTrigger();
    });
    document.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      const trigger = event.target.closest?.("[data-open-newsletter-popup][role='button']");
      if (!trigger) return;
      event.preventDefault();
      this.openFromTrigger();
    });

    // Fixed button to reopen popup
    this.fixedButton = document.getElementById(`NewsletterFixedButton-${this.id.replace('NewsletterModal-', '')}`);
    if (this.fixedButton) {
      this.fixedButton.addEventListener("click", this.openFromTrigger.bind(this));
    }

    this.querySelector(".js-code-copy")?.addEventListener("click", this.copyCode.bind(this));
    this.querySelector(".js-code-continue")?.addEventListener("click", this.continueWithCode.bind(this));
    this.querySelector(".js-code-close")?.addEventListener("click", () => {
      this.hide();
      this.track("code_close");
      this.updateFixedButtonVisibility();
    });
  }

  init() {
    this.delay = this.dataset.delay * 1000;
    // Days to remember each answer (section settings; 0 = until the browser is closed).
    this.closedDays = Number(this.dataset.closedDays) || 0;
    this.subscribedDays = Number(this.dataset.subscribedDays) || 0;
    this.emailVisitorDays = Number(this.dataset.emailVisitorDays) || 0;
    // "No thanks" belongs to one offer: a new offer version (e.g. Black Friday) is shown once again.
    this.closedCookie = this.dataset.offerVersion ? `newsletter-closed-${this.dataset.offerVersion}` : "newsletter-closed";
    this.rememberEmailVisitor();

    this.closed = getCookie(this.closedCookie);
    this.subscribed = getCookie("newsletter-subscribed");
    // Safari keeps cookies set by JavaScript for 7 days at most: renew "subscribed" on every visit
    // (not "No thanks", which must expire).
    if (this.subscribed === "true") setCookie("newsletter-subscribed", "true", this.subscribedDays);
    this.form = this.querySelector(".js-form");
    this.formPanel = this.querySelector("[data-form-panel]");
    this.codePanel = this.querySelector("[data-code-panel]");
    this.ageVerificationPopup = document.querySelector("age-verification-popup");
    this.hasScrolledEnough = false;
  }

  track(name) {
    window.sferaTrack?.event(name);
  }

  setState(state) {
    window.sferaTrack?.set("popup_state", state);
  }

  // Visitors from our emails are already on the list: no popup for them. It is a guess, not their
  // choice, so it lasts this visit only; links from Klaviyo (_kx, utm_source=klaviyo) are remembered
  // longer only if the visitor accepted preference cookies. Only "true" is stored, never the _kx value.
  rememberEmailVisitor() {
    const params = new URLSearchParams(window.location.search);
    const fromKlaviyo = params.has("_kx") || (params.get("utm_source") || "").toLowerCase() === "klaviyo";
    const fromEmail = fromKlaviyo || (params.get("utm_medium") || "").toLowerCase() === "email";
    if (!fromEmail) return;

    setCookie("newsletter-email-visitor", "true");
    if (!fromKlaviyo || !this.emailVisitorDays || typeof window.Shopify?.loadFeatures !== "function") return;
    window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
      if (!error && window.Shopify.customerPrivacy?.preferencesProcessingAllowed?.()) {
        setCookie("newsletter-email-visitor", "true", this.emailVisitorDays);
      }
    });
  }

  isSubscribed() {
    return getCookie("newsletter-subscribed") === "true";
  }

  // People who must never get the popup on their own, nor the "10€ OFF" floating button.
  isKnownSubscriberOrCustomer() {
    return (
      this.isSubscribed() ||
      getCookie("newsletter-email-visitor") === "true" ||
      this.dataset.customerOrdered === "true"
    );
  }

  initialState() {
    if (this.isSubscribed()) return "subscribed";
    if (getCookie("newsletter-email-visitor") === "true") return "email_visitor";
    if (this.dataset.customerOrdered === "true") return "customer";
    if (this.closed === "true") return "closed";
    return "none";
  }

  // "No thanks" is remembered for days; a tap outside or Escape (maybe by accident) only for this visit.
  // ModalDialog may have hidden the popup already ("No thanks" button, Escape): hide() noted it.
  closeNewsletter(method) {
    if (this.hasAttribute("open")) this.hide();
    if (!this.closingForm) return;
    this.closingForm = false;

    setCookie(this.closedCookie, "true", method === "button" ? this.closedDays : 0);
    this.closed = "true";
    window.sferaTrack?.set("popup_close_by", method);
    this.track("popup_close");
    this.setState("closed");
    this.updateFixedButtonVisibility();
  }

  // Announcement bar "10€ OFF…" and the floating button. Subscribers get their code, the rest the form.
  openFromTrigger() {
    this.track("popup_bar_open");
    if (this.isSubscribed() && this.codePanel) {
      this.showCode();
    } else {
      this.showForm();
    }
    this.updateFixedButtonVisibility();
  }

  showForm({ auto = false } = {}) {
    this.showingCode = false;
    if (this.formPanel) this.formPanel.hidden = false;
    if (this.codePanel) this.codePanel.hidden = true;
    this.show();
    this.track("popup_shown");
    if (auto) this.track("popup_auto_shown");
    this.setState("shown");
  }

  updateFixedButtonVisibility() {
    if (!this.fixedButton) return;

    const isPopupOpen = this.hasAttribute("open");

    // Show button only when:
    // 1. User has scrolled more than 100vh
    // 2. Popup is closed
    // 3. User hasn't subscribed (nor comes from our emails, nor is a customer with orders)
    // 4. The footer's own sign-up form is not on screen (Jorge, 20-oct-2026: there it is redundant and
    //    covers the copyright line; only the back-to-top arrow stays)
    if (this.hasScrolledEnough && !isPopupOpen && !this.footerFormVisible && !this.isKnownSubscriberOrCustomer()) {
      this.fixedButton.classList.add("is-visible");
    } else {
      this.fixedButton.classList.remove("is-visible");
    }
  }

  handleScroll() {
    const scrollPosition = window.scrollY;
    const viewportHeight = window.innerHeight;

    this.hasScrolledEnough = scrollPosition > viewportHeight;
    this.updateFixedButtonVisibility();
  }

  connectedCallback() {
    if (Shopify.designMode) {
      if (this.dataset.openInDesignMode === "true") {
      this.show();
      }
      this.updateFixedButtonVisibility();
      return;
    }

    // The initial state is read from cookies: sent to Clarity only with analytics consent.
    const initialState = this.initialState();
    window.sferaTrack?.whenAnalytics?.(() => this.setState(initialState));

    if (!this.handleSignUpResult()) {
      this.handlePopupVisibility();
    }
    // Set initial button visibility
    setTimeout(() => this.updateFixedButtonVisibility(), 100);

    // Watch for popup state changes to update button visibility
    this.setupFixedButtonObserver();

    // Attach scroll listener for fixed button visibility
    if (this.fixedButton) {
      this.scrollHandler = this.handleScroll.bind(this);
      window.addEventListener("scroll", this.scrollHandler, { passive: true });
      // Check initial scroll position
      this.handleScroll();
      this.watchFooterForm();
    }
  }

  // The fixed "10€ OFF" button hides while the footer's sign-up form is on screen.
  watchFooterForm() {
    const footerForm = document.querySelector(".footer__newsletter");
    if (!footerForm || !("IntersectionObserver" in window)) return;
    this.footerObserver = new IntersectionObserver(entries => {
      this.footerFormVisible = entries.some(entry => entry.isIntersecting);
      this.updateFixedButtonVisibility();
    });
    this.footerObserver.observe(footerForm);
  }

  disconnectedCallback() {
    // Clean up scroll listener
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
    }
    this.footerObserver?.disconnect();
  }

  setupFixedButtonObserver() {
    if (!this.fixedButton) return;

    const observer = new MutationObserver(() => {
      this.updateFixedButtonVisibility();
    });

    observer.observe(this, { attributes: true, attributeFilter: ['open'] });
  }

  handlePopupVisibility() {
    if (getCookie("age-verified") !== "true" && this.ageVerificationPopup) {
      this.handleAgeVerificationPopup();
    } else {
      this.showPopupWithDelay();
    }
  }

  handleAgeVerificationPopup() {
    let prevClassState = this.classList.contains("newsletter-popup-is-hidden");

    const observer = new MutationObserver(mutations => {
      const mutation = mutations[0];
      if (mutation.attributeName !== "class") return;

      const currentClassState = mutation.target.classList.contains("newsletter-popup-is-hidden");

      if (prevClassState !== currentClassState) {
        prevClassState = currentClassState;
        if (!currentClassState) {
          this.showPopupWithDelay();
        }
      }
    });

    observer.observe(this, { attributes: true });
  }

  // Opens by itself only for people who haven't answered, aren't on the list or customers,
  // and don't already carry a discount code (e.g. from an influencer link).
  shouldShowAutomatically() {
    return (
      this.closed !== "true" &&
      !this.isKnownSubscriberOrCustomer() &&
      !getCookie("discount_code") &&
      !window.sferaDiscount?.activeCode
    );
  }

  showPopupWithDelay() {
    if (!this.shouldShowAutomatically()) return;

    this.whenConsentAnswered(() => {
      setTimeout(() => {
        this.whenConsentBannerClosed(() => {
          if (this.shouldShowAutomatically() && !this.hasAttribute("open")) {
            this.showForm({ auto: true });
          }
        });
      }, this.delay);
    });
  }

  // Red de seguridad: si la API de consentimiento falla o responde antes de tiempo
  // (pasó en Firefox Focus), no abrimos el popup mientras el banner siga en la página.
  // Cuando el visitante responde, Shopify no quita #shopify-pc__banner: lo deja con display: none.
  whenConsentBannerClosed(callback) {
    const isBannerOpen = () => {
      const banner = document.getElementById("shopify-pc__banner");
      return Boolean(banner) && getComputedStyle(banner).display !== "none";
    };

    if (!isBannerOpen()) {
      callback();
      return;
    }

    const interval = setInterval(() => {
      if (isBannerOpen()) return;
      clearInterval(interval);
      setTimeout(callback, 1000);
    }, 500);
  }

  // Si el banner de cookies de Shopify está esperando respuesta, el popup no empieza
  // a contar hasta que el visitante elige; así no se pisan (sobre todo en móvil).
  whenConsentAnswered(callback) {
    if (typeof window.Shopify?.loadFeatures !== "function") {
      callback();
      return;
    }

    window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
      // shouldShowBanner() solo dice si la región exige banner, no si este visitante ya
      // respondió: para quien vuelve tras aceptar o rechazar sigue siendo true.
      const privacy = window.Shopify.customerPrivacy;
      const consentPending = Boolean(privacy?.shouldShowBanner()) && privacy.getTrackingConsent?.() === "no_interaction";

      if (error || !consentPending) {
        callback();
        return;
      }

      document.addEventListener("visitorConsentCollected", () => callback(), { once: true });
    });
  }

  // The form is a normal Shopify customer form: the page reloads with ?customer_posted=true.
  // Before leaving, note where the visitor was so we can bring them back there.
  onSubscribe(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.querySelector("[data-sfera-newsletter]")) return;

    const fromPopup = this.contains(form);
    try {
      sessionStorage.setItem(
        "sfera-newsletter-pending",
        JSON.stringify({
          path: window.location.pathname,
          scrollY: window.scrollY,
          fromPopup,
          time: Date.now()
        })
      );
    } catch (error) {}
    if (fromPopup) this.track("popup_submit");
  }

  takePendingSignUp() {
    try {
      const pending = JSON.parse(sessionStorage.getItem("sfera-newsletter-pending") || "null");
      sessionStorage.removeItem("sfera-newsletter-pending");
      if (pending && Date.now() - pending.time < 30 * 60 * 1000) return pending;
    } catch (error) {}
    return null;
  }

  // Back from the sign-up: show the code (and apply it), or the form again with its error. The page is
  // covered until then (snippets/sfera-signup-veil.liquid): lift the cover once the popup is up.
  liftSignUpVeil() {
    const root = document.documentElement;
    if (!root.classList.contains("sfera-signup-return")) return;
    root.classList.add("sfera-signup-return--out");
    setTimeout(() => root.classList.remove("sfera-signup-return", "sfera-signup-return--out"), 300);
  }

  handleSignUpResult() {
    const url = new URL(window.location.href);
    if (url.searchParams.get("customer_posted") !== "true") {
      this.liftSignUpVeil();
      return false;
    }

    // snippets/newsletter-form.liquid marks every newsletter form with the result of the sign-up.
    const statuses = Array.from(document.querySelectorAll("[data-newsletter-status]"), (element) => element.dataset.newsletterStatus);
    const status = statuses.includes("success") ? "success" : statuses.includes("error") ? "error" : "";

    // Only with our note from the sign-up in this tab: Shopify says "success" for any address with
    // ?customer_posted=true, even without a sign-up (a shared link would hand out the code). The note
    // also says where to go back to and that it was a newsletter form (not, e.g., early access).
    const pending = this.takePendingSignUp();
    if (!pending) {
      this.liftSignUpVeil();
      return false;
    }

    if (status === "success") {
      setCookie("newsletter-subscribed", "true", this.subscribedDays);
      this.subscribed = "true";
    }

    // Clean the URL and go back to where the visitor was, not to the form's anchor (Shopify comes
    // back to "#…newsletter-popup" and the browser jumps there).
    url.searchParams.delete("customer_posted");
    history.replaceState(history.state, "", url.pathname + url.search);
    let scrollRestored = Promise.resolve();
    if (pending.path === window.location.pathname) {
      const restore = () => window.scrollTo(0, pending.scrollY || 0);
      restore();
      scrollRestored = new Promise((resolve) => {
        const restoreLater = () => setTimeout(() => {
          restore();
          resolve();
        }, 50);
        if (document.readyState === "complete") restoreLater();
        else window.addEventListener("load", restoreLater, { once: true });
      });
    }

    if (status === "success") {
      this.track("popup_subscribed");
      this.setState("subscribed");
      if (this.codePanel) {
        // Only once the page is back in place: the open popup locks the page where it is
        // (on iPhone it pins it, and closing the popup returns to that same spot).
        this.whenConsentBannerClosed(() =>
          scrollRestored.then(() => {
            this.showCode();
            this.liftSignUpVeil();
          })
        );
        return true;
      }
      scrollRestored.then(() => this.liftSignUpVeil());
      return false;
    }

    if (status === "error" && pending.fromPopup) {
      scrollRestored.then(() => {
        this.showForm();
        this.liftSignUpVeil();
      });
      return true;
    }
    scrollRestored.then(() => this.liftSignUpVeil());
    return false;
  }

  // The welcome code on screen, and applied to the cart at the same time, unless the visitor already
  // carries another code (e.g. an influencer's, maybe better): then only the button applies it.
  showCode() {
    const discount = window.sferaDiscount;
    const code = discount?.welcomeCode;
    if (!this.codePanel || !code) return;

    // The code is not in the page's HTML: write it, and the /discount link, now.
    this.codePanel.querySelector("[data-code-value]").textContent = code;
    const continueLink = this.codePanel.querySelector(".js-code-continue");
    if (continueLink) {
      const redirect = continueLink.dataset.redirect || "/products/sferaboard";
      continueLink.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(redirect)}`;
    }

    const heading = this.codePanel.querySelector("[data-code-heading]");
    if (heading?.dataset.template) {
      heading.textContent = heading.dataset.template.replace("[amount]", discount.welcomeLabel());
    }

    const active = discount.activeCode;
    const otherCode = Boolean(active) && active !== code.toUpperCase();
    const text = this.codePanel.querySelector("[data-code-text]");
    if (text) {
      // "Already applied: you'll see [amount] off at checkout", with the amount as in the heading.
      if (!text.dataset.textAuto) text.dataset.textAuto = text.textContent.trim();
      const template = otherCode ? text.dataset.textManual : text.dataset.textAuto;
      text.textContent = template.replace("[amount]", discount.welcomeLabel());
    }

    this.showingCode = true;
    if (this.formPanel) this.formPanel.hidden = true;
    this.codePanel.hidden = false;
    this.show();
    this.track("code_shown");

    if (otherCode) {
      this.codeApplied = Promise.resolve(false);
    } else if (active) {
      this.codeApplied = Promise.resolve(true);
    } else {
      this.codeApplied = discount.apply(code).then(
        () => true,
        () => false
      );
    }
  }

  show(opener) {
    this.closingForm = false;
    super.show(opener);
  }

  hide() {
    this.closingForm = this.hasAttribute("open") && !this.showingCode;
    super.hide();
    this.showingCode = false;
  }

  copyCode(event) {
    const button = event.currentTarget;
    const code = this.codePanel.querySelector("[data-code-value]")?.textContent.trim();
    if (!code) return;

    const copied = () => {
      if (!button.dataset.label) button.dataset.label = button.textContent.trim();
      button.textContent = button.dataset.copiedLabel || button.dataset.label;
      clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => (button.textContent = button.dataset.label), 2000);
    };
    const fallback = () => {
      const range = document.createRange();
      range.selectNodeContents(this.codePanel.querySelector("[data-code-value]"));
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      try {
        if (document.execCommand("copy")) copied();
      } catch (error) {}
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(copied, fallback);
    } else {
      fallback();
    }
    this.track("code_copy");
  }

  // The link is /discount/CODE?redirect=… (set in showCode). When the code is already applied, go to
  // BUY NOW on the product page (or to the board) without reloading; otherwise the link applies it.
  async continueWithCode(event) {
    this.track("code_apply_tap");
    event.preventDefault();
    const href = event.currentTarget.href;
    const applied = await (this.codeApplied || Promise.resolve(false));
    if (!applied) {
      window.location.href = href;
      return;
    }
    if (document.querySelector("product-info")) {
      this.hide();
      this.updateFixedButtonVisibility();
      this.scrollToBuyNow();
    } else {
      window.location.href = window.sferaDiscount?.welcomeUrl || href;
    }
  }

  // Continue = go to where the code is used (Jorge, 20-oct-2026: subscribing from the footer form left him
  // at the bottom of the page). On phones, the end of the gallery and the three options with BESTSELLER at
  // the top, under the fixed header, with BUY NOW below them (Jorge, 21-oct-2026: centring BUY NOW cut the
  // first option); on computers, BUY NOW in the middle of the screen.
  scrollToBuyNow() {
    const target =
      document.querySelector("product-info buy-now-button") ||
      document.querySelector("product-info .product-form__submit");
    if (!target) return;
    const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
    // After the popup has closed and the page lock has put the page back where it was.
    setTimeout(() => {
      const options = document.querySelector("product-info .product__variant-picker");
      if (window.innerWidth < 750 && options) {
        const header = document.querySelector("header.sfera-header") || document.querySelector("#header");
        const headerHeight = header ? header.getBoundingClientRect().height : 0;
        // 44px of the gallery's end (the image and its progress bar) above the options.
        const top = options.getBoundingClientRect().top + window.scrollY - headerHeight - 44;
        window.scrollTo({ top: Math.max(0, top), behavior });
        return;
      }
      target.scrollIntoView({ behavior, block: "center" });
    }, 400);
  }
}

customElements.define("newsletter-popup", NewsletterPopup);
